import { useEffect, useLayoutEffect, useRef, useState } from "react";

import styles from "./composerAttachments.module.css";
import { MediaLightbox } from "./MediaLightbox";
import {
  type ApiAttachment,
  attachmentContentPath,
  deleteAttachment,
  MAX_ATTACHMENT_BYTES,
  MAX_ATTACHMENTS_PER_MESSAGE,
  previewKind,
  uploadAttachment,
} from "../data/api/attachments";
import { ApiError } from "../data/api/client";
import { Icon } from "../icons";
import { cx } from "../lib/cx";

// The composer half of chat attachments (chat.md §Composer UI), shared by the
// two composers that take files — the task chat's and the New Agent page's.
// Files upload the moment they arrive, each as a chip carrying its own
// progress and failure; submitting claims the settled ids. The submit gating
// itself stays with each composer — what "can send" means differs between a
// chat message and a launch — this module owns everything up to it.

/**
 * A file in the composer, from drop/paste to claimed-by-a-message. Uploading
 * starts the moment the file arrives — per-file progress, failures surfaced
 * before send — and submit only claims the settled ones.
 */
export type ComposerAttachment = {
  readonly error?: string;
  /**
   * Absent for a chip restored from a persisted draft (`chatDrafts.ts`) —
   * a reload has no way to hand back the bytes a `File` wraps, only the
   * `uploaded` record of what the server already has. Always present
   * otherwise; only a restored chip's `status` is ever `"uploaded"` without
   * one.
   */
  readonly file?: File;
  readonly localId: string;
  /** What the chip shows, and what a click on it opens. Null for a file the
   * client never previews — an SVG, an archive, a log. */
  readonly previewKind?: "image" | "video";
  /** Object URL for a previewable file's chip thumbnail; revoked on removal. */
  readonly previewUrl?: string;
  /**
   * True when the caps refused the file at drop time (too large, too many).
   * A refusal is not a failed attempt: it gets no Retry, which would only
   * work around the rule the chip itself states — remove is the way out.
   */
  readonly refused?: boolean;
  readonly status: "error" | "uploaded" | "uploading";
  readonly uploaded?: ApiAttachment;
};

export const formatBytes = (n: number): string =>
  n >= 1024 * 1024
    ? `${(n / (1024 * 1024)).toFixed(1)} MB`
    : n >= 1024
      ? `${Math.round(n / 1024).toString()} KB`
      : `${n.toString()} B`;

// A chip's name/size come from its `File` while one is present, and from the
// server's own record of it — the only thing a restored chip has — once it
// isn't.
export const attachmentName = (a: ComposerAttachment): string =>
  a.file?.name || a.uploaded?.name || "pasted image";

export const attachmentBytes = (a: ComposerAttachment): number =>
  a.file?.size ?? a.uploaded?.bytes ?? 0;

export type UseComposerAttachments = {
  /** From a drop, a paste, or the picker; refusals become error chips. */
  readonly addFiles: (files: Iterable<File>) => void;
  readonly attachments: ReadonlyArray<ComposerAttachment>;
  /** After a successful submit: revoke previews and empty the tray. */
  readonly clear: () => void;
  /** Whether a drag is over the composer; the page renders the highlight. */
  readonly dragOver: boolean;
  /** The settled uploads a submit would claim, in chip order. */
  readonly readyAttachments: ReadonlyArray<ComposerAttachment>;
  readonly removeChip: (localId: string) => void;
  readonly retryChip: (localId: string) => void;
  readonly setDragOver: (over: boolean) => void;
  /** No chip uploading or errored — the tray is claimable as-is. */
  readonly uploadsSettled: boolean;
};

const restoredChip = (uploaded: ApiAttachment): ComposerAttachment => ({
  localId: `restored-${uploaded.public_id}`,
  previewKind: previewKind(uploaded.mime) ?? undefined,
  // Not an object URL — there is no `File` to make one from — but the same
  // bytes are one round trip away by id (chat.md §Attachments), same as an
  // already-sent message's own thumbnail.
  previewUrl: previewKind(uploaded.mime)
    ? attachmentContentPath(uploaded.public_id)
    : undefined,
  status: "uploaded",
  uploaded,
});

const isApiAttachment = (v: unknown): v is ApiAttachment =>
  typeof v === "object" &&
  v !== null &&
  typeof (v as Partial<ApiAttachment>).public_id === "string";

const loadStoredAttachments = (key: string): ApiAttachment[] => {
  try {
    const raw = localStorage.getItem(key);
    const parsed: unknown = raw === null ? [] : JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isApiAttachment) : [];
  } catch {
    return [];
  }
};

const saveStoredAttachments = (
  key: string,
  list: ReadonlyArray<ApiAttachment>,
): void => {
  try {
    if (list.length === 0) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, JSON.stringify(list));
    }
  } catch {
    // Storage disabled or full — the chips just won't survive this mount.
  }
};

/**
 * The attachment tray's state machine. `workspaceId` scopes the uploads
 * (`api.md` §Chat attachments); with `enabled` false — no workspace resolved
 * yet, or the composer closed — arriving files are ignored, which is what a
 * composer without attach affordances expects of a stray drop.
 *
 * `storageKey`, when given, persists settled chips under it (`chatDrafts.ts`)
 * and restores them on mount — and re-restores, rather than leaving the
 * previous key's chips in place, whenever `storageKey` itself changes under
 * an already-mounted composer. A chip still uploading or errored at that
 * moment has no `File` to persist and is simply dropped, same as on an
 * ordinary unmount.
 */
export function useComposerAttachments(
  workspaceId: string | undefined,
  enabled: boolean,
  storageKey?: string,
): UseComposerAttachments {
  const [attachments, setAttachments] = useState<
    ReadonlyArray<ComposerAttachment>
  >(() =>
    storageKey ? loadStoredAttachments(storageKey).map(restoredChip) : [],
  );
  const [dragOver, setDragOver] = useState(false);
  const nextLocalId = useRef(0);
  const loadedKeyRef = useRef(storageKey);

  useLayoutEffect(() => {
    if (loadedKeyRef.current === storageKey) {
      return;
    }
    loadedKeyRef.current = storageKey;
    setAttachments(
      storageKey ? loadStoredAttachments(storageKey).map(restoredChip) : [],
    );
  }, [storageKey]);

  useEffect(() => {
    if (!storageKey) {
      return;
    }
    saveStoredAttachments(
      storageKey,
      attachments
        .filter((a) => a.status === "uploaded")
        .map((a) => a.uploaded!),
    );
  }, [attachments, storageKey]);

  const updateChip = (
    localId: string,
    patch: Partial<ComposerAttachment>,
  ): void => {
    setAttachments((prev) =>
      prev.map((a) => (a.localId === localId ? { ...a, ...patch } : a)),
    );
  };

  const startUpload = (localId: string, file: File): void => {
    if (workspaceId === undefined) {
      return;
    }
    void uploadAttachment(workspaceId, file)
      .then((uploaded) => updateChip(localId, { status: "uploaded", uploaded }))
      .catch((err: unknown) =>
        updateChip(localId, {
          error: err instanceof ApiError ? err.message : "Upload failed.",
          status: "error",
        }),
      );
  };

  // Uploads start the moment files arrive — from a drop, a paste, or the
  // picker — so submit stays the small JSON round-trip it always was. A file
  // over the cap becomes an error chip at drop time, with the reason where
  // the file is, rather than a 4xx at submit time.
  const addFiles = (files: Iterable<File>): void => {
    if (workspaceId === undefined || !enabled) {
      return;
    }
    const room =
      MAX_ATTACHMENTS_PER_MESSAGE -
      attachments.filter((a) => a.status !== "error").length;
    const additions: ComposerAttachment[] = [];
    for (const file of files) {
      nextLocalId.current += 1;
      const localId = `att-${nextLocalId.current.toString()}`;
      const refusal =
        file.size > MAX_ATTACHMENT_BYTES
          ? "Too large — the limit is 20 MB per file."
          : additions.length >= room
            ? `Too many files — ${MAX_ATTACHMENTS_PER_MESSAGE.toString()} per message.`
            : undefined;
      additions.push({
        error: refusal,
        file,
        localId,
        previewKind: previewKind(file.type) ?? undefined,
        previewUrl: previewKind(file.type)
          ? URL.createObjectURL(file)
          : undefined,
        refused: refusal !== undefined,
        status: refusal === undefined ? "uploading" : "error",
      });
    }
    setAttachments((prev) => [...prev, ...additions]);
    for (const a of additions) {
      if (a.status === "uploading") {
        // Every element just built above carries the `file` it was made
        // from — only a restored chip, never one of these, lacks one.
        startUpload(a.localId, a.file!);
      }
    }
  };

  const removeChip = (localId: string): void => {
    const chip = attachments.find((a) => a.localId === localId);
    if (chip?.previewUrl) {
      URL.revokeObjectURL(chip.previewUrl);
    }
    if (chip?.uploaded) {
      // Best-effort: an orphan the delete misses is what the stale sweep is
      // for (background-jobs.md §Cleanup).
      void deleteAttachment(chip.uploaded.public_id).catch(() => undefined);
    }
    setAttachments((prev) => prev.filter((a) => a.localId !== localId));
  };

  const retryChip = (localId: string): void => {
    const chip = attachments.find((a) => a.localId === localId);
    if (!chip || chip.status !== "error" || chip.refused) {
      return;
    }
    updateChip(localId, { error: undefined, status: "uploading" });
    // Only `addFiles` ever creates an "error" chip, always with its `file` —
    // a restored chip (no `file`) is always "uploaded", never "error".
    startUpload(localId, chip.file!);
  };

  const clear = (): void => {
    for (const a of attachments) {
      if (a.previewUrl) {
        URL.revokeObjectURL(a.previewUrl);
      }
    }
    setAttachments([]);
  };

  return {
    addFiles,
    attachments,
    clear,
    dragOver,
    readyAttachments: attachments.filter((a) => a.status === "uploaded"),
    removeChip,
    retryChip,
    setDragOver,
    uploadsSettled: attachments.every((a) => a.status === "uploaded"),
  };
}

/** The chip tray rendered above a composer's textarea. */
export const AttachmentChips = ({
  attachments,
  onRemove,
  onRetry,
}: {
  readonly attachments: ReadonlyArray<ComposerAttachment>;
  readonly onRemove: (localId: string) => void;
  readonly onRetry: (localId: string) => void;
}) => {
  // Which chip's image is popped out, held as an id rather than the chip
  // itself: removing a file revokes its object URL, and looking the chip up
  // again each render is what closes the viewer instead of leaving it pointed
  // at bytes that no longer exist.
  const [enlargedId, setEnlargedId] = useState<string | null>(null);
  const enlarged = attachments.find(
    (a) => a.localId === enlargedId && a.previewUrl !== undefined,
  );

  if (attachments.length === 0) {
    return null;
  }
  return (
    <div className={styles.attachChips}>
      {attachments.map((a) => (
        <div
          className={cx(
            styles.attachChip,
            a.status === "error" && styles.attachChipError,
          )}
          key={a.localId}
          title={a.error ?? attachmentName(a)}
        >
          {a.previewUrl ? (
            // 22px is enough to say "a file is attached" and not enough to
            // check *which* screenshot you pasted — which you want to know
            // before sending it, not after. Same viewer the thread uses
            // (chat.md §Attachments); only a previewable type ever has a
            // previewUrl, so the allow-list holds here too.
            <button
              className={styles.attachThumbButton}
              onClick={() => setEnlargedId(a.localId)}
              title="Enlarge"
              type="button"
            >
              {a.previewKind === "video" ? (
                // Muted and unplayed: at this size the frame says which clip
                // it is, and a chip tray that starts playing by itself is a
                // composer nobody can read.
                <video
                  className={styles.attachThumb}
                  muted
                  preload="metadata"
                  src={`${a.previewUrl}#t=0.001`}
                />
              ) : (
                <img alt="" className={styles.attachThumb} src={a.previewUrl} />
              )}
            </button>
          ) : (
            <Icon.Doc size={13} />
          )}
          <span className={styles.attachName}>{attachmentName(a)}</span>
          <span className={styles.attachMeta}>
            {a.status === "uploading"
              ? "uploading…"
              : a.status === "error"
                ? (a.error ?? "failed")
                : formatBytes(attachmentBytes(a))}
          </span>
          {a.status === "error" && !a.refused ? (
            <button
              className={styles.attachAction}
              onClick={() => onRetry(a.localId)}
              title="Retry upload"
              type="button"
            >
              <Icon.Refresh size={11} />
            </button>
          ) : null}
          <button
            className={styles.attachAction}
            onClick={() => onRemove(a.localId)}
            title="Remove"
            type="button"
          >
            <Icon.X size={11} />
          </button>
        </div>
      ))}
      {enlarged?.previewUrl ? (
        <MediaLightbox
          caption={formatBytes(attachmentBytes(enlarged))}
          filename={attachmentName(enlarged)}
          kind={enlarged.previewKind ?? "image"}
          name={attachmentName(enlarged)}
          onClose={() => setEnlargedId(null)}
          src={enlarged.previewUrl}
        />
      ) : null}
    </div>
  );
};
